import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingCart, User, Menu, MessageCircle } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { fmtTRY } from "../../lib/format";
import { trackEvent } from "../../lib/analytics";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";

export default function Header() {
  const { count } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [q, setQ] = useState("");
  const [sug, setSug] = useState([]);
  const [sugOpen, setSugOpen] = useState(false);

  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setSug([]); setSugOpen(false); return; }
    const id = setTimeout(() => {
      api.get("/search/suggest", { params: { q: t } }).then(({ data }) => { setSug(data.items || []); setSugOpen(true); }).catch(() => {});
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  const highlight = (label) => {
    const t = q.trim();
    if (!t) return label;
    const idx = label.toLocaleLowerCase("tr").indexOf(t.toLocaleLowerCase("tr"));
    if (idx < 0) return label;
    return (<>{label.slice(0, idx)}<b className="text-forest">{label.slice(idx, idx + t.length)}</b>{label.slice(idx + t.length)}</>);
  };

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/settings/public").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    setSugOpen(false);
    if (q.trim()) navigate(`/urunler?q=${encodeURIComponent(q.trim())}`);
  };

  const menuItems = settings?.menu_items?.length
    ? settings.menu_items
    : [{ label: "Tüm Ürünler", url: "/urunler" }, ...categories.map((c) => ({ label: c.name, url: `/urunler?kategori=${c.slug}` }))];

  const waLink = settings?.whatsapp
    ? `https://wa.me/${settings.whatsapp}?text=${encodeURIComponent("Merhaba, Ata Angora ürünleri hakkında bilgi almak istiyorum.")}`
    : "#";

  const today = new Date().toISOString().slice(0, 10);
  const campOn = settings?.hero_campaign_announcement
    && (!settings?.hero_campaign_start || today >= settings.hero_campaign_start)
    && (!settings?.hero_campaign_end || today <= settings.hero_campaign_end);
  const annText = campOn ? settings.hero_campaign_announcement : (settings?.announcement || "750 TL üzeri siparişlerde kargo ücretsiz");

  return (
    <header className="sticky top-0 z-40">
      <div className="bg-forest text-cream text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-4">
          <p data-testid="announcement-bar" className="truncate">{annText}</p>
          <div className="hidden sm:flex items-center gap-4 shrink-0">
            <Link to="/soru-cevap" data-testid="header-qa-link" className="font-semibold text-gold-light hover:text-gold transition-colors whitespace-nowrap">Soru &amp; Cevap</Link>
            <Link to="/b2b" data-testid="header-b2b-link" className="font-semibold text-gold-light hover:text-gold transition-colors whitespace-nowrap">B2B Toptan Satış</Link>
          </div>
        </div>
      </div>
      <div className="bg-white/90 backdrop-blur-md border-b border-stone-200/60">
        <div className="w-full px-4 sm:px-6 lg:px-10 pt-3 pb-4">
          <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3">
            <div className="md:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <button data-testid="mobile-menu-button" className="p-2 -ml-2" aria-label="Menü"><Menu className="w-6 h-6 text-forest" /></button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 bg-cream">
                  <div className="mt-8 flex flex-col gap-1">
                    {menuItems.map((m, i) => (
                      <Link key={i} to={m.url} data-testid={`mobile-menu-item-${i}`} className="py-2.5 px-3 rounded-lg text-stone-800 hover:bg-cream-dark font-medium">{m.label}</Link>
                    ))}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
            <div className="hidden md:block" />
            <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5 justify-self-start shrink-0">
              <img src={settings?.logo_url || "/logo.jpg"} alt="Ata Angora Doğal Gıdalar" className="h-11 w-11 md:h-14 md:w-14 rounded-full object-cover ring-2 ring-gold/40 shadow-sm" />
              <div className="leading-tight">
                <span className="font-serif text-lg md:text-xl font-bold text-forest block">Ata Angora</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Doğal Gıdalar</span>
              </div>
            </Link>
            <nav className="flex items-center gap-1 sm:gap-2 justify-self-end">
              <a href={waLink} target="_blank" rel="noreferrer" data-testid="header-whatsapp-button" className="hidden md:flex items-center gap-2 bg-[#25D366] hover:bg-[#20ba5a] text-white text-xs font-semibold px-3.5 py-2.5 rounded-xl transition-colors">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <Link to={user ? "/hesap" : "/giris"} data-testid="header-account-link" className="flex flex-col items-center p-2 rounded-xl hover:bg-cream-dark transition-colors">
                <User className="w-5 h-5 text-forest" />
                <span className="text-[10px] text-stone-600 hidden sm:block">{user ? "Hesabım" : "Giriş"}</span>
              </Link>
              <Link to="/favoriler" data-testid="header-favorites-link" className="flex flex-col items-center p-2 rounded-xl hover:bg-cream-dark transition-colors">
                <Heart className="w-5 h-5 text-forest" />
                <span className="text-[10px] text-stone-600 hidden sm:block">Favoriler</span>
              </Link>
              <Link to="/sepet" data-testid="header-cart-link" className="relative flex flex-col items-center p-2 rounded-xl hover:bg-cream-dark transition-colors">
                <ShoppingCart className="w-5 h-5 text-forest" />
                <span className="text-[10px] text-stone-600 hidden sm:block">Sepet</span>
                {count > 0 && <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 bg-terra text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{count}</span>}
              </Link>
            </nav>
          </div>
          <form onSubmit={submitSearch} className="mt-3 w-full max-w-2xl mx-auto">
            <div className="relative">
              <input
                data-testid="search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onFocus={() => sug.length > 0 && setSugOpen(true)}
                onBlur={() => setTimeout(() => setSugOpen(false), 180)}
                onKeyDown={(e) => e.key === "Escape" && setSugOpen(false)}
                placeholder="Bal, tahin, pekmez... ürün arayın"
                className="w-full h-12 pl-4 pr-12 rounded-xl border border-stone-300 bg-cream/60 focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest text-sm shadow-sm"
              />
              <button data-testid="search-submit" type="submit" className="absolute right-1.5 top-1.5 h-9 w-9 rounded-lg bg-forest text-white flex items-center justify-center hover:bg-forest-dark transition-colors" aria-label="Ara">
                <Search className="w-4 h-4" />
              </button>
              {sugOpen && q.trim().length >= 2 && (
                <div data-testid="search-suggestions" className="absolute left-0 right-0 top-full mt-2 bg-white border border-stone-200 rounded-2xl shadow-xl overflow-hidden z-50">
                  {sug.map((s, i) => s.type === "category" ? (
                    <Link key={`c-${i}`} to={`/urunler?kategori=${s.slug}`} data-testid={`search-suggestion-${i}`} onClick={() => { setSugOpen(false); setQ(""); }} className="flex items-center gap-3 px-4 py-2.5 hover:bg-cream transition-colors">
                      <img src={s.image} alt="" className="w-10 h-10 rounded-lg object-cover bg-cream-dark" />
                      <p className="flex-1 text-sm font-medium text-stone-800 truncate">{highlight(s.label)}</p>
                      <span className="text-[11px] font-semibold text-gold-dark bg-gold/10 px-2 py-1 rounded-lg shrink-0">Kategori</span>
                    </Link>
                  ) : (
                    <Link key={`p-${i}`} to={`/urun/${s.slug}`} data-testid={`search-suggestion-${i}`} onClick={() => { setSugOpen(false); setQ(""); }} className="flex items-center gap-3 px-4 py-2.5 hover:bg-cream transition-colors">
                      <img src={s.image} alt="" className="w-10 h-10 rounded-lg object-cover bg-cream-dark" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-800 truncate">{highlight(s.label)}</p>
                        <p className={`text-[11px] ${s.stock > 0 ? "text-emerald-700" : "text-terra"}`}>{s.stock > 0 ? "Stokta var" : "Stokta yok"}</p>
                      </div>
                      <span className="text-sm font-bold text-forest shrink-0">{fmtTRY(s.price)}</span>
                    </Link>
                  ))}
                  {sug.length === 0 && <p className="px-4 py-3 text-sm text-stone-400">Sonuç bulunamadı</p>}
                  <button type="submit" data-testid="search-suggestion-all" className="w-full px-4 py-2.5 text-left text-sm font-semibold text-forest hover:bg-cream border-t border-stone-100 transition-colors">"{q.trim()}" için tüm sonuçlar →</button>
                </div>
              )}
            </div>
          </form>
        </div>
        <nav className="hidden lg:block border-t border-stone-100">
          <div className="max-w-7xl mx-auto px-4 flex justify-center gap-1 overflow-x-auto scrollbar-none">
            {menuItems.map((m, i) => (
              <Link key={i} to={m.url} data-testid={`nav-menu-item-${i}`} className={`px-3 py-2.5 text-sm whitespace-nowrap transition-colors ${i === 0 ? "font-semibold text-forest hover:bg-cream-dark rounded-b-lg" : "text-stone-600 hover:text-forest"}`}>{m.label}</Link>
            ))}
          </div>
        </nav>
      </div>
    </header>
  );
}
